import React, { useState } from "react";
import { Box, Typography, Stack, Fade } from "@mui/material";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";

export default function FloatingQRButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* ── Backdrop ── */}
      <Fade in={open}>
        <Box
          onClick={() => setOpen(false)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(15, 23, 42, 0.3)",
            backdropFilter: "blur(6px)",
            zIndex: 1298,
          }}
        />
      </Fade>

      {/* ── Popup Panel ── */}
      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 88, md: 96 },
          right: { xs: 16, md: 28 },
          zIndex: 1299,
          transformOrigin: "bottom right",
          transform: open ? "scale(1) translateY(0)" : "scale(0.85) translateY(12px)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease",
        }}
      >
        <Box
          sx={{
            width: { xs: 300, sm: 320 },
            borderRadius: "28px",
            overflow: "hidden",
            bgcolor: "#ffffff",
            boxShadow:
              "0 32px 80px rgba(15,23,42,0.18), 0 8px 24px rgba(15,23,42,0.10), 0 0 0 1px rgba(0,0,0,0.04)",
          }}
        >
          {/* ── Top accent bar ── */}
          <Box
            sx={{
              height: 4,
              background: "linear-gradient(90deg, #1B4ED8 0%, #72BF20 100%)",
            }}
          />

          {/* ── Header ── */}
          <Box
            sx={{
              px: 3,
              pt: 2.5,
              pb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #f1f5f9",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              {/* Icon badge */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #1B4ED8 0%, #3b6ff0 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(27,78,216,0.30)",
                }}
              >
                <QrCodeScannerIcon sx={{ color: "#fff", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: "#0f172a",
                    fontWeight: 800,
                    fontSize: 15,
                    fontFamily: "'Space Grotesk', sans-serif",
                    lineHeight: 1.2,
                    letterSpacing: "-0.01em",
                  }}
                >
                  Download RideX City
                </Typography>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: 12,
                    fontFamily: "'Space Grotesk', sans-serif",
                    mt: 0.2,
                  }}
                >
                  Free app — Android &amp; iOS
                </Typography>
              </Box>
            </Stack>

            {/* Close button */}
            <Box
              onClick={() => setOpen(false)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                bgcolor: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": { bgcolor: "#e2e8f0" },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 16, color: "#64748b" }} />
            </Box>
          </Box>

          {/* ── QR Code Section ── */}
          <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
            {/* QR wrapper with gradient border */}
            <Box
              sx={{
                position: "relative",
                borderRadius: "20px",
                p: "3px",
                background: "linear-gradient(135deg, #1B4ED8, #72BF20)",
                boxShadow: "0 8px 24px rgba(27,78,216,0.15)",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: "18px",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {/* QR Image */}
                <Box
                  component="img"
                  src="/assets/images/app-qr-code.png"
                  alt="Scan to download RideX City app"
                  sx={{
                    width: "100%",
                    display: "block",
                    aspectRatio: "1 / 1",
                    objectFit: "cover",
                  }}
                />

                {/* Animated scan line */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 10,
                    right: 10,
                    height: "2px",
                    borderRadius: "2px",
                    background:
                      "linear-gradient(90deg, transparent 0%, #1B4ED8 35%, #72BF20 65%, transparent 100%)",
                    boxShadow: "0 0 12px rgba(114,191,32,0.8)",
                    animation: "qrScan 2.4s ease-in-out infinite",
                    "@keyframes qrScan": {
                      "0%": { top: "8%", opacity: 0 },
                      "15%": { opacity: 1 },
                      "85%": { opacity: 1 },
                      "100%": { top: "88%", opacity: 0 },
                    },
                  }}
                />

                {/* Corner brackets */}
                {[
                  { top: 10, left: 10 },
                  { top: 10, right: 10 },
                  { bottom: 10, left: 10 },
                  { bottom: 10, right: 10 },
                ].map((pos, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      position: "absolute",
                      ...pos,
                      width: 22,
                      height: 22,
                      borderColor: "#1B4ED8",
                      borderStyle: "solid",
                      borderWidth: 0,
                      ...(pos.top !== undefined && pos.left !== undefined
                        ? { borderTopWidth: 3, borderLeftWidth: 3, borderRadius: "5px 0 0 0" }
                        : pos.top !== undefined
                        ? { borderTopWidth: 3, borderRightWidth: 3, borderRadius: "0 5px 0 0" }
                        : pos.left !== undefined
                        ? { borderBottomWidth: 3, borderLeftWidth: 3, borderRadius: "0 0 0 5px" }
                        : { borderBottomWidth: 3, borderRightWidth: 3, borderRadius: "0 0 5px 0" }),
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Instruction */}
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography
                sx={{
                  color: "#0f172a",
                  fontSize: 13.5,
                  fontWeight: 700,
                  fontFamily: "'Space Grotesk', sans-serif",
                  letterSpacing: "-0.01em",
                }}
              >
                Point your camera at the code
              </Typography>
              <Typography
                sx={{
                  color: "#94a3b8",
                  fontSize: 12,
                  mt: 0.4,
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                Opens instantly — no browser needed
              </Typography>
            </Box>
          </Box>

          {/* ── Steps ── */}
          <Box sx={{ px: 3, pb: 3 }}>
            <Box
              sx={{
                borderRadius: "16px",
                bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0",
                overflow: "hidden",
              }}
            >
              {[
                { num: "1", label: "Scan QR with your camera" },
                { num: "2", label: "Install app in 60 seconds" },
                { num: "3", label: "Unlock any EV near you" },
              ].map((step, i, arr) => (
                <Box
                  key={i}
                  sx={{
                    px: 2,
                    py: 1.4,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    borderBottom: i < arr.length - 1 ? "1px solid #e2e8f0" : "none",
                    transition: "background 0.2s",
                    "&:hover": { bgcolor: "#f1f5f9" },
                  }}
                >
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: "8px",
                      background:
                        i === 0
                          ? "linear-gradient(135deg, #1B4ED8, #3b6ff0)"
                          : i === 1
                          ? "linear-gradient(135deg, #72BF20, #9ad646)"
                          : "linear-gradient(135deg, #0f172a, #1e293b)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      boxShadow:
                        i === 0
                          ? "0 2px 8px rgba(27,78,216,0.3)"
                          : i === 1
                          ? "0 2px 8px rgba(114,191,32,0.3)"
                          : "0 2px 8px rgba(15,23,42,0.2)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 800,
                        fontFamily: "'Space Grotesk', sans-serif",
                      }}
                    >
                      {step.num}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: "#334155",
                      fontSize: 13,
                      fontWeight: 600,
                      fontFamily: "'Space Grotesk', sans-serif",
                      flex: 1,
                    }}
                  >
                    {step.label}
                  </Typography>
                  <ArrowForwardIosRoundedIcon sx={{ color: "#cbd5e1", fontSize: 11 }} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Floating Trigger Button ── */}
      <Box
        onClick={() => setOpen((prev) => !prev)}
        id="floating-qr-btn"
        sx={{
          position: "fixed",
          bottom: { xs: 24, md: 32 },
          right: { xs: 16, md: 28 },
          zIndex: 1300,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          // Pill shape when closed, circle when open
          px: open ? 0 : { xs: 2, sm: 2.5 },
          width: open ? { xs: 52, md: 56 } : "auto",
          height: { xs: 52, md: 56 },
          borderRadius: open ? "50%" : "50px",
          background: open
            ? "#0f172a"
            : "linear-gradient(135deg, #1B4ED8 0%, #2563eb 100%)",
          boxShadow: open
            ? "0 8px 24px rgba(15,23,42,0.35)"
            : "0 8px 28px rgba(27,78,216,0.45), 0 2px 8px rgba(27,78,216,0.2)",
          transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
          justifyContent: "center",
          "&:hover": {
            transform: "translateY(-3px)",
            boxShadow: open
              ? "0 12px 32px rgba(15,23,42,0.45)"
              : "0 14px 36px rgba(27,78,216,0.55)",
          },
          // Subtle pulse ring when closed
          "&::after": !open
            ? {
                content: '""',
                position: "absolute",
                inset: -5,
                borderRadius: "50px",
                border: "2px solid rgba(27,78,216,0.35)",
                animation: "softPulse 2.5s ease-in-out infinite",
              }
            : {},
          "@keyframes softPulse": {
            "0%, 100%": { opacity: 0.6, transform: "scale(1)" },
            "50%": { opacity: 0, transform: "scale(1.12)" },
          },
        }}
      >
        {open ? (
          <CloseRoundedIcon sx={{ color: "#fff", fontSize: 22 }} />
        ) : (
          <>
            <QrCodeScannerIcon sx={{ color: "#fff", fontSize: 22, flexShrink: 0 }} />
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                fontFamily: "'Space Grotesk', sans-serif",
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
                display: { xs: "none", sm: "block" },
              }}
            >
              Get the App
            </Typography>
          </>
        )}
      </Box>
    </>
  );
}
